import { ReactNode } from 'react';
import { CircleCheckIcon } from 'lucide-react';
import { useStaticAssetsUrl } from '@tinct/core-utils';
import {
  Alert,
  AlertDescription,
  Button,
  ButtonProps,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@tinct/web-components';

export type ChangesNotLiveAction = {
  label: ReactNode;
  onClick: () => void;
  variant?: ButtonProps['variant'];
  isLoading?: boolean;
  disabled?: boolean;
};

export type ChangesNotLiveModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Inline (bolded) description of what was edited, e.g. "the value proposition". */
  step: ReactNode;
  /** Message shown in the info alert, e.g. "All 47 variant pages need to be updated.". */
  alertDescription: ReactNode;
  /** Footer actions. Pass one button (acknowledge) or two (continue + generate). */
  actions: ChangesNotLiveAction[];
  /** Closing call-to-action line. Defaults to the "Generate and review…" copy. */
  callToAction?: ReactNode;
};

export const ChangesNotLiveModal = ({
  open,
  onOpenChange,
  step,
  alertDescription,
  actions,
  callToAction = 'Generate and review the new version of your variant pages.',
}: ChangesNotLiveModalProps) => {
  const assetsUrl = useStaticAssetsUrl();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        className="max-w-md gap-0 overflow-hidden rounded-3xl p-0"
      >
        {/* Cover image */}
        <div className="px-2 pt-2">
          <div className="overflow-hidden rounded-2xl bg-amber-300">
            <img
              src={`${assetsUrl}/changes-not-live.png`}
              alt=""
              className="aspect-[2/1] w-full object-cover object-bottom"
            />
          </div>
        </div>

        {/* Head */}
        <div className="flex flex-col gap-3 p-8 text-center">
          <DialogTitle className="font-heading text-2xl font-semibold tracking-[-0.4px] text-foreground">
            These changes aren&apos;t live yet
          </DialogTitle>
          <div className="flex flex-col gap-4 text-base text-muted-foreground">
            <p>
              You edited <strong className="font-bold text-foreground">{step}</strong>. Your
              existing variants were generated from the previous version, so visitors still see
              the old copy.
            </p>
            <p>{callToAction}</p>
          </div>
        </div>

        {/* Alert */}
        <div className="px-8 pb-6">
          <Alert className="items-center rounded-2xl border-indigo-100 bg-indigo-50 p-4">
            <CircleCheckIcon className="text-indigo-600" />
            <AlertDescription className="text-indigo-600">
              {alertDescription}
            </AlertDescription>
          </Alert>
        </div>

        {/* Footer */}
        <DialogFooter className="flex-row gap-2 px-8 pb-8">
          {actions.map((action, index) => (
            <Button
              key={index}
              size="lg"
              variant={action.variant ?? (index === 0 && actions.length > 1 ? 'outline' : 'default')}
              className="flex-1"
              onClick={action.onClick}
              isLoading={action.isLoading}
              disabled={action.disabled}
            >
              {action.label}
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
